import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Clock, Users, ArrowUpRight, Check, Hourglass } from "lucide-react";
import { toast } from "sonner";
import AddonsSelector from "@/components/AddonsSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicActivity() {
    const { id } = useParams();
    const [activity, setActivity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
    const [transportChoice, setTransportChoice] = useState(null);
    const [accommodationDates, setAccommodationDates] = useState([]);
    const [statusText, setStatusText] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/activities/${id}`);
                if (alive) setActivity(data);
            } catch {
                if (alive) setNotFound(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [id]);

    const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
            return toast.error("Name, email and phone are required");
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/public/activities/${id}/rsvp`, {
                ...form,
                transport_choice: transportChoice,
                accommodation_dates: accommodationDates,
            });
            setSubmitted(true);
            setActivity(data.activity);
            setStatusText(data.status === "waitlist" ? "You're on the waitlist." : "You're confirmed.");
            toast.success(data.status === "waitlist" ? "Added to waitlist" : "RSVP confirmed");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "RSVP failed");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8] text-[#888885] text-sm tracking-[0.15em] uppercase">
                Loading
            </div>
        );
    }
    if (notFound) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8] p-10">
                <div className="max-w-md w-full border border-[#E5E5E3] bg-white p-10 text-center">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Not found</p>
                    <h2 className="font-heading text-2xl font-medium text-[#111110]">This event isn&apos;t available.</h2>
                    <p className="text-sm text-[#666664] mt-3">It may have been deleted, or the link is no longer public.</p>
                </div>
            </div>
        );
    }

    const max = activity.max_signups || 0;
    const isFull = max > 0 && activity.signup_count >= max;
    const isPast = !!activity.is_past;
    const isClosed = !!activity.is_closed;

    return (
        <div className="min-h-screen bg-[#F9F9F8]">
            <header className="border-b border-[#E5E5E3] bg-white">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src="https://customer-assets.emergentagent.com/job_group-calendar-7/artifacts/qy8qz9tb_SF%20WF%20logo%20%281%29.png"
                            alt="Scratchfirst"
                            className="h-8 w-auto"
                        />
                        <span className="hidden sm:inline text-[10px] tracking-[0.2em] text-[#888885] uppercase pl-3 border-l border-[#E5E5E3]">public invite</span>
                    </div>
                    <Link to="/login" className="text-xs tracking-[0.15em] uppercase text-[#666664] hover:text-[#111110] inline-flex items-center gap-1.5" data-testid="employee-signin-link">
                        Employee sign-in <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </header>

            <main className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10 lg:py-16 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10">
                <section>
                    <div className="border border-[#E5E5E3] bg-white" style={{ borderTop: `4px solid ${activity.color || "#F0F0EE"}` }}>
                        <div className="p-8 lg:p-10">
                            {activity.tag && <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-3" data-testid="public-activity-tag">{activity.tag}</p>}
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#111110] leading-[1.05]" data-testid="public-activity-title">{activity.title}</h1>
                            <p className="text-sm text-[#666664] mt-3" data-testid="public-activity-host">Hosted by {activity.creator_name}</p>

                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Info icon={<Clock className="w-4 h-4" strokeWidth={1.5} />} label="When">
                                    <span data-testid="public-activity-time">
                                        {format(new Date(activity.start_time), "EEE, MMM d · h:mm a")}<br />
                                        <span className="text-[#888885] text-xs">to {format(new Date(activity.end_time), "EEE, MMM d · h:mm a")}</span>
                                    </span>
                                </Info>
                                {activity.location && (
                                    <Info icon={<MapPin className="w-4 h-4" strokeWidth={1.5} />} label="Where">
                                        <span data-testid="public-activity-location">{activity.location}</span>
                                    </Info>
                                )}
                                <Info icon={<Users className="w-4 h-4" strokeWidth={1.5} />} label="Capacity">
                                    <span data-testid="public-activity-capacity">
                                        {activity.signup_count}{max > 0 ? ` / ${max}` : ""} confirmed
                                        {activity.waitlist_count > 0 && ` · ${activity.waitlist_count} waitlisted`}
                                    </span>
                                </Info>
                                {activity.signup_deadline && (
                                    <Info icon={<Hourglass className="w-4 h-4" strokeWidth={1.5} />} label={isPast ? "Ended" : isClosed ? "Closed" : "RSVP by"}>
                                        <span data-testid="public-activity-deadline" className={isClosed ? "text-[#999996]" : undefined}>
                                            {format(new Date(activity.signup_deadline), "EEE, MMM d · h:mm a")}
                                        </span>
                                    </Info>
                                )}
                            </div>

                            {activity.description && (
                                <div className="mt-8 pt-8 border-t border-[#E5E5E3]">
                                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-3">About</p>
                                    <p className="text-[15px] text-[#333333] leading-relaxed whitespace-pre-wrap" data-testid="public-activity-description">{activity.description}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <aside>
                    <div className="border border-[#E5E5E3] bg-white p-8 lg:sticky lg:top-10" data-testid="public-rsvp-card">
                        {submitted ? (
                            <div className="text-center py-6">
                                <div className="w-12 h-12 bg-[#111110] inline-flex items-center justify-center mb-4">
                                    <Check className="w-6 h-6 text-white" strokeWidth={2} />
                                </div>
                                <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Confirmation</p>
                                <h3 className="font-heading text-2xl font-light text-[#111110]" data-testid="rsvp-success">{statusText}</h3>
                                <p className="text-sm text-[#666664] mt-3">We&apos;ve recorded your RSVP. Look out for any updates from the host directly.</p>
                            </div>
                        ) : isPast ? (
                            <div className="text-center py-6" data-testid="rsvp-ended">
                                <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Event ended</p>
                                <h3 className="font-heading text-2xl font-light text-[#111110]">This event is in the past.</h3>
                                <p className="text-sm text-[#666664] mt-3">Thanks for stopping by. Check the host for photos or a future session.</p>
                            </div>
                        ) : isClosed ? (
                            <div className="text-center py-6" data-testid="rsvp-closed">
                                <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Registration closed</p>
                                <h3 className="font-heading text-2xl font-light text-[#111110]">Sorry, RSVPs have closed.</h3>
                                <p className="text-sm text-[#666664] mt-3">The deadline has passed. Reach out to the host directly if you still want to attend.</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">RSVP</p>
                                <h3 className="font-heading text-2xl font-light text-[#111110]">{isFull ? "Join the waitlist" : "Tell us you're coming"}</h3>
                                <p className="text-sm text-[#666664] mt-2 mb-6">No account needed. Your info goes only to the host.</p>
                                <form onSubmit={submit} className="space-y-4" data-testid="public-rsvp-form">
                                    <Field label="Name">
                                        <Input value={form.name} onChange={e => setField("name", e.target.value)} placeholder="Jane Doe" data-testid="rsvp-name" required
                                            className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                                    </Field>
                                    <Field label="Email">
                                        <Input type="email" value={form.email} onChange={e => setField("email", e.target.value)} placeholder="jane@example.com" data-testid="rsvp-email" required
                                            className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                                    </Field>
                                    <Field label="Phone">
                                        <Input type="tel" value={form.phone} onChange={e => setField("phone", e.target.value)} placeholder="+1 555 0100" data-testid="rsvp-phone" required
                                            className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                                    </Field>
                                    <Field label="Note (optional)">
                                        <Textarea value={form.note} onChange={e => setField("note", e.target.value)} placeholder="Anything the host should know" data-testid="rsvp-note"
                                            className="rounded-none border-[#E5E5E3] min-h-[72px] resize-none focus-visible:ring-1 focus-visible:ring-[#111110]" />
                                    </Field>
                                    <AddonsSelector
                                        activity={activity}
                                        transportChoice={transportChoice}
                                        onTransportChoice={setTransportChoice}
                                        accommodationDates={accommodationDates}
                                        onAccommodationDates={setAccommodationDates}
                                        idPrefix="public-rsvp"
                                    />
                                    <Button type="submit" disabled={busy}
                                        className="w-full h-12 rounded-none bg-[#111110] hover:bg-[#333333] text-white font-medium text-base tracking-tight"
                                        data-testid="rsvp-submit-btn">
                                        {isFull ? "Join waitlist" : "Confirm RSVP"}
                                    </Button>
                                    <p className="text-xs text-[#888885] text-center pt-1">By RSVPing, you agree to share your contact details with the event host.</p>
                                </form>
                            </>
                        )}
                    </div>
                </aside>
            </main>
        </div>
    );
}

function Info({ icon, label, children }) {
    return (
        <div className="border border-[#E5E5E3] p-4">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">
                <span className="text-[#666664]">{icon}</span>{label}
            </div>
            <div className="text-sm text-[#111110] leading-relaxed">{children}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2 block font-medium">{label}</Label>
            {children}
        </div>
    );
}
